import React, { useState, useEffect } from "react";
import { apiClient } from "../../apiclient/apiclient";
import { Search, ChevronDown, ChevronRight, Download, X, RotateCcw } from "lucide-react";
import { toast } from "react-toastify";
import ThemedTable from "../../components/ThemedTable";
import Pagination from "../../components/Pagination";
import Loader from "../../components/Loader";

const FOLLOWUP_DAYS = 30;

const NutritionalFollowUp = () => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [search, setSearch] = useState("");
  const [daysFilter, setDaysFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 200;
  
  const [expandedRowId, setExpandedRowId] = useState(null);

  const toggleRow = (id) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/clients-follow-up");
      if (res.data.success) {
        setFollowUps(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching follow ups:", error);
      toast.error("Failed to fetch nutritional follow ups");
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      const queryParams = new URLSearchParams({
        search: search || "",
        daysFilter: daysFilter || "all",
        cityFilter: cityFilter || ""
      }).toString();
      const res = await apiClient.get(`/clients-follow-up/export/excel?${queryParams}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `nutritional_follow_up.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Excel downloaded successfully");
    } catch (error) {
      console.error("Error downloading excel:", error);
      toast.error("Failed to download excel");
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  // Filter Data
  const filteredFollowUps = followUps.filter((item) => {
    if (item.daysRemaining > FOLLOWUP_DAYS) return false;

    if (daysFilter === "expired" && item.daysRemaining >= 0) return false;
    if (daysFilter === "15" && (item.daysRemaining > 15 || item.daysRemaining < 0)) return false;
    if (daysFilter === "7" && (item.daysRemaining > 7 || item.daysRemaining < 0)) return false;
    if (daysFilter === "3" && (item.daysRemaining > 3 || item.daysRemaining < 0)) return false;

    const clientName = item.client?.name || "";
    const phone = item.client?.phone || "";
    const city = item.client?.city || "";
    const productName = item.productName || "";
    
    if (cityFilter && city !== cityFilter) return false;
    
    const q = search.toLowerCase();
    
    return (
      clientName.toLowerCase().includes(q) ||
      phone.includes(q) ||
      productName.toLowerCase().includes(q)
    );
  });

  // Group by client
  const clientGroups = [];
  const clientMap = new Map();

  const availableCities = [...new Set(followUps.map(item => item.client?.city).filter(Boolean))].sort();

  filteredFollowUps.forEach(item => {
    const clientId = item.client?._id;
    if (!clientId) return;

    if (!clientMap.has(clientId)) {
      clientMap.set(clientId, {
        client: item.client,
        items: []
      });
      clientGroups.push(clientMap.get(clientId));
    }
    clientMap.get(clientId).items.push(item);
  });

  // Pagination
  const totalPages = Math.ceil(clientGroups.length / itemsPerPage);
  const currentData = clientGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-0">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Nutritional Follow Up</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track when clients will run out of their supplements
          </p>
        </div>
      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="bg-white border-b border-slate-200">
        <div className="flex flex-col sm:flex-row gap-2 w-full items-stretch p-4">
          {/* SEARCH */}
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              placeholder="Search by client, phone, or product..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-10 py-2 w-full h-10 text-sm bg-white border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm text-slate-800"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-2">
            <select
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-10 px-3 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition min-w-[120px] text-slate-700 font-medium shadow-sm"
            >
              <option value="">All Cities</option>
              {availableCities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              value={daysFilter}
              onChange={(e) => {
                setDaysFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-10 px-3 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition text-slate-700 font-medium shadow-sm"
            >
              <option value="all">All (30 Days)</option>
              <option value="15">In 15 Days</option>
              <option value="7">In 7 Days</option>
              <option value="3">In 3 Days</option>
              <option value="expired">Expired</option>
            </select>

            <button
              onClick={downloadExcel}
              disabled={isDownloadingExcel}
              className="w-10 h-10 bg-[#172554] text-white rounded-md hover:bg-[#1e2f6a] transition-all flex items-center justify-center shadow-sm shrink-0 active:scale-95 disabled:opacity-50"
              title="Export Excel"
            >
              {isDownloadingExcel ? (
                <Loader inline={true} text="" />
              ) : (
                <Download size={18} />
              )}
            </button>

            <button
              onClick={() => {
                setSearch("");
                setCityFilter("");
                setDaysFilter("all");
                setCurrentPage(1);
              }}
              className="w-10 h-10 bg-[#172554] text-white rounded-md border border-[#172554] hover:bg-[#1e2f6a] transition-all flex items-center justify-center shadow-sm shrink-0 active:scale-95"
              title="Reset Filters"
            >
              <RotateCcw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-none bg-white flex-1 min-h-0 flex flex-col border-x-0 border-b-0">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <ThemedTable className="text-left border-collapse w-full">
            <thead>
              <tr className="bg-white text-gray-700 border-b border-slate-200">
                <th className="p-3 w-10 text-center"></th>
                <th className="p-3 text-left whitespace-nowrap">Client Name</th>
                <th className="p-3 text-left whitespace-nowrap">Phone</th>
                <th className="p-3 text-left whitespace-nowrap">Company</th>
                <th className="p-3 text-left whitespace-nowrap">City</th>
                <th className="p-3 text-left whitespace-nowrap">Items Due</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center">
                    <Loader text="Analyzing nutritional schedules..." size={32} />
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((group, index) => (
                  <React.Fragment key={group.client._id}>
                    <tr
                      onClick={() => toggleRow(group.client._id)}
                      className={`cursor-pointer transition-colors border-b border-slate-100 last:border-0 ${
                        expandedRowId === group.client._id ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="p-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(group.client._id);
                          }}
                          className="p-1 rounded-md hover:bg-slate-200 transition-colors"
                        >
                          {expandedRowId === group.client._id ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                        </button>
                      </td>
                      <td className="p-3">
                        <div className="font-semibold text-slate-800">
                          {group.client.name || "Unknown"}
                        </div>
                      </td>
                      <td className="p-3 text-sm font-medium text-slate-600">
                        {group.client.phone || "-"}
                      </td>
                      <td className="p-3 text-sm text-slate-600">
                        {group.client.companyName || "-"}
                      </td>
                      <td className="p-3 text-sm text-slate-600">
                        {group.client.city || "-"}
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200/60">
                          {group.items.length} Product(s)
                        </span>
                      </td>
                    </tr>
                    
                    {/* Expanded Row */}
                    {expandedRowId === group.client._id && (
                      <tr className="border-b bg-slate-50/10">
                        <td colSpan="6" className="p-0">
                          <div className="overflow-x-auto w-full">
                            <table className="w-full text-left border-collapse bg-white">
                              <thead>
                                <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px] font-bold">
                                  <th className="p-3 w-10 text-center"></th>
                                  <th className="p-3 text-left">Product</th>
                                  <th className="p-3 text-left">Order ID</th>
                                  <th className="p-3 text-left">Purchased On</th>
                                  <th className="p-3 text-left">Days Left</th>
                                  <th className="p-3 text-left">Est. End Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.items.map((item, idx) => (
                                  <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40 transition-colors">
                                    <td className="p-3 w-10 text-center"></td>
                                    <td className="p-3">
                                      <div className="text-sm font-medium text-slate-800">{item.productName}</div>
                                      <div className="text-xs text-slate-500">Qty: {item.quantity} | {item.servingDuration}</div>
                                    </td>
                                    <td className="p-3 text-xs font-semibold text-slate-500">
                                      {item.orderId || "-"}
                                    </td>
                                    <td className="p-3 text-sm text-slate-600">
                                      {new Date(item.orderDate).toLocaleDateString()}
                                    </td>
                                    <td className="p-3">
                                      {item.daysRemaining > 7 ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                          {item.daysRemaining} Days
                                        </span>
                                      ) : item.daysRemaining > 0 ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                          {item.daysRemaining} Days
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                          Ended {-item.daysRemaining}d ago
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3 text-sm text-slate-600">
                                      {new Date(item.followUpDate).toLocaleDateString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                    No follow-ups found
                  </td>
                </tr>
              )}
            </tbody>
          </ThemedTable>
        </div>
        
        {/* Pagination */}
        {!loading && currentData.length > 0 && (
          <div className="border-t border-slate-200 py-4 flex justify-center bg-white rounded-b-lg">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NutritionalFollowUp;
